import { useMemo } from "react";

import { crossesCustomsBorder } from "@/constants/countries";
import { resolveDutyGroup } from "@/constants/dutyGroups";
import {
  ImportChargeEstimate,
  estimateImportCharges,
} from "@/constants/importCharges";
import { useDeliveryLocation } from "@/features/delivery-location/hooks/useDeliveryLocation";

type CategoryLike = { slug?: string | null } | null | undefined;

type UseImportChargeEstimateArgs = {
  /** Seller's country. The parcel origin. */
  originCountry?: string | null;
  /** Item price in EUR. */
  itemPrice?: number | null;
  /** Delivery price in EUR, when known. */
  shippingPrice?: number | null;
  /** Root-first category list; `breadcrumbs` or `categories` both work. */
  breadcrumbs?: readonly CategoryLike[] | null;
};

/**
 * The import charge the buyer will be billed on delivery, or null.
 *
 * The destination is the site's delivery location (WHO-5226), the same one the
 * PDP shipping block quotes against: the signed-in buyer's chosen saved
 * address, or the address a guest entered themselves. Never the browser locale
 * or an IP guess. That is the whole point: this number is only meaningful for
 * a buyer in GB, NO or CH, and showing a customs estimate to the other 99% of
 * visitors would be noise on every product page in Europe. A visitor with no
 * delivery location gets null.
 *
 * Returns null rather than zero whenever we cannot stand behind a figure, so
 * callers can render nothing instead of rendering "EUR 0.00".
 */
export const useImportChargeEstimate = ({
  originCountry,
  itemPrice,
  shippingPrice,
  breadcrumbs,
}: UseImportChargeEstimateArgs): ImportChargeEstimate | null => {
  const { address } = useDeliveryLocation();
  const destinationCountry = address?.country;

  return useMemo(() => {
    if (!destinationCountry || itemPrice == null) {
      return null;
    }

    // A GB seller shipping to a GB buyer clears no border, so no charge is due
    // even though the destination is one we model.
    if (!crossesCustomsBorder(originCountry, destinationCountry)) {
      return null;
    }

    return estimateImportCharges({
      destinationCountry,
      itemPrice,
      shippingPrice: shippingPrice ?? 0,
      dutyGroup: resolveDutyGroup(breadcrumbs),
    });
  }, [
    destinationCountry,
    originCountry,
    itemPrice,
    shippingPrice,
    breadcrumbs,
  ]);
};

export default useImportChargeEstimate;
